/**
 * @file Reads "what is happening in the match" for the layered music.
 *
 * The counterpart to {@link ./VisualIndicatorSystem.js}, which shows the
 * player when an enemy has noticed them; this is the reverse — whether the
 * player can currently see an enemy — plus three more perception checks
 * (moving, shooting, recent kill) and the kill-timer's low-health threshold.
 *
 * This system owns the gameplay-world half of the contract: it queries
 * components and emits one edge-triggered event,
 * {@link Events.MUSIC_CUE_CHANGED}. It knows nothing about Web Audio.
 * {@link ../audio/MusicDirector.js} owns the other half: it knows nothing
 * about the ECS and only reacts to that event. Neither could reuse the
 * other's job without inheriting things it does not need.
 */

import { System } from '../ecs/System.js';
import { Transform, Velocity, Weapon, Health, AIBrain, Collider, PlayerControlled } from '../components/index.js';
import { Layer } from '../config/Layers.js';
import { Events } from '../core/EventBus.js';
import { raycastAABB } from '../physics/AABB.js';
import { angleDelta, toRadians } from '../utils/MathUtils.js';

export class MusicCueSystem extends System {
  static priority = 96;

  constructor() {
    super();
    /** @type {number} `ctx.time.elapsed` at the player's last confirmed kill. @private */
    this._lastKillElapsed = -Infinity;
    /**
     * Last tuple actually emitted, so the event only fires on a real change.
     * @type {{ scratch: boolean, synth: boolean, kick: boolean, bass2: boolean, pulse: boolean }}
     * @private
     */
    this._emitted = { scratch: false, synth: false, kick: false, bass2: false, pulse: false };
    /** @type {any[]} Scratch array of nearby static walls, reused every frame. @private */
    this._walls = [];
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
  }

  /** @returns {void} */
  init() {
    this._subscriptions.push(
      this.ctx.events.on(Events.DIED, (payload) => this._onDied(payload))
    );
  }

  /**
   * A confirmed player kill keeps BASS2 in for a grace window. Filtering on
   * `source` rather than a dedicated "kill" event mirrors how `HealthSystem`
   * already refills the kill timer off the same payload.
   *
   * @param {{ entity: import('../ecs/Entity.js').Entity,
   *           source: import('../ecs/Entity.js').Entity | null }} payload Death event.
   * @returns {void}
   * @private
   */
  _onDied({ entity, source }) {
    if (!source || source === entity || !source.alive || !source.has(PlayerControlled)) return;
    if (!entity.has(AIBrain)) return; // Only enemy kills count.
    this._lastKillElapsed = this.ctx.time.elapsed;
  }

  /**
   * @param {number} _dt Delta time in seconds; unused, every check reads
   *   current component state directly.
   * @returns {void}
   */
  update(_dt) {
    const player = this.ctx.player;
    if (!player?.alive) return;

    const transform = player.get(Transform);
    const velocity = player.get(Velocity);
    const weapon = player.get(Weapon);
    const health = player.get(Health);
    const settings = this.ctx.config.music;

    const scratch = velocity.linear.lengthSq() > settings.moveSpeedThreshold ** 2;
    const kick = Boolean(weapon?.triggerHeld);
    const bass2 = this.ctx.time.elapsed - this._lastKillElapsed < settings.killGraceDuration;

    let pulse = false;
    if (health?.usesKillTimer && !health.dead) {
      const ratio = health.killTimerMax > 0 ? health.killTimer / health.killTimerMax : 0;
      pulse = ratio > 0 && ratio <= this.ctx.config.hud.lowKillTimerThreshold;
    }

    const synth = this._enemyInSight(transform, settings);

    this._emitIfChanged({ scratch, synth, kick, bass2, pulse });
  }

  /**
   * Whether any live enemy sits inside the player's sight radius and cone,
   * with no wall between them. Distance and angle are cheap and checked
   * first; the wall raycast — the expensive part — only runs for candidates
   * that already pass both.
   *
   * @param {import('../components/Transform.js').Transform} playerTransform Player transform.
   * @param {import('../config/GameConfig.js').GameConfig['music']} settings Music tunables.
   * Queries every AIBrain carrier; that set is only ever live enemies (a few
   * dozen at peak), so a radius pre-filter would cost more than it saves.
   * @returns {boolean}
   * @private
   */
  _enemyInSight(playerTransform, settings) {
    const enemies = this.world.query(AIBrain, Transform, Health);
    if (enemies.length === 0) return false;

    const origin = playerTransform.position;
    const radiusSq = settings.sightRadius * settings.sightRadius;
    const halfFov = toRadians(settings.sightFovDegrees) / 2;
    let wallsCollected = false;

    for (const enemy of enemies) {
      if (!enemy.alive || enemy.get(Health).dead) continue;

      const position = enemy.get(Transform).position;
      const dx = position.x - origin.x;
      const dz = position.z - origin.z;
      const distanceSq = dx * dx + dz * dz;
      if (distanceSq > radiusSq) continue;

      const angleToEnemy = Math.atan2(dx, dz);
      if (Math.abs(angleDelta(playerTransform.rotationY, angleToEnemy)) > halfFov) continue;

      if (!wallsCollected) {
        this._collectWalls(origin, settings.sightRadius);
        wallsCollected = true;
      }
      if (!this._blocked(origin, position)) return true;
    }
    return false;
  }

  /**
   * Caches static walls near the player, so the occlusion test below runs
   * against a handful of boxes instead of the whole level. Same technique as
   * `StreamFlameSystem._collectWalls`.
   *
   * @param {import('three').Vector3} origin Player position.
   * @param {number} radius Sight radius.
   * @returns {void}
   * @private
   */
  _collectWalls(origin, radius) {
    this._walls.length = 0;
    for (const entity of this.world.query(Collider, Transform)) {
      const collider = entity.get(Collider);
      if (!collider.isStatic || (collider.layer & Layer.WALL) === 0) continue;

      const position = entity.get(Transform).position;
      const reach = radius + Math.max(collider.halfX, collider.halfZ);
      const dx = position.x - origin.x;
      const dz = position.z - origin.z;
      if (dx * dx + dz * dz > reach * reach) continue;

      this._walls.push(entity);
    }
  }

  /**
   * @param {import('three').Vector3} origin Player position.
   * @param {import('three').Vector3} target Enemy position.
   * @returns {boolean} True when a wall stands between them.
   * @private
   */
  _blocked(origin, target) {
    const dx = target.x - origin.x;
    const dz = target.z - origin.z;

    for (const entity of this._walls) {
      const collider = entity.get(Collider);
      const position = entity.get(Transform).position;
      const t = raycastAABB(
        origin.x,
        origin.z,
        dx,
        dz,
        position.x - collider.halfX,
        position.z - collider.halfZ,
        position.x + collider.halfX,
        position.z + collider.halfZ
      );
      if (t >= 0 && t < 0.995) return true;
    }
    return false;
  }

  /**
   * @param {{ scratch: boolean, synth: boolean, kick: boolean, bass2: boolean, pulse: boolean }} next
   *   Freshly computed cue state.
   * @returns {void}
   * @private
   */
  _emitIfChanged(next) {
    const prev = this._emitted;
    if (
      prev.scratch === next.scratch &&
      prev.synth === next.synth &&
      prev.kick === next.kick &&
      prev.bass2 === next.bass2 &&
      prev.pulse === next.pulse
    ) {
      return;
    }

    this._emitted = next;
    this.ctx.events.emit(Events.MUSIC_CUE_CHANGED, next);
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
  }
}
